import { type ReactNode } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Form } from 'react-bootstrap';
import { type FieldErrors, FormProvider, useForm, type UseFormReturn } from 'react-hook-form';

import { configHookForm } from './config';

type HookFormValidationMode = 'onBlur' | 'onChange' | 'onSubmit' | 'onTouched' | 'all';

export interface HookFormResolverProps {
  onSubmit?: (data: any, reset: any) => void;
  onInvalid?: HookFormInvalidHandler;
  validations?: any;
  children?: (methods: HookFormMethods) => ReactNode;
  initialValues?: any;
  nameForm?: string;
  validationMode?: HookFormValidationMode;
}

export type HookFormMethods = UseFormReturn<any>;
export type HookFormInvalidHandler = (errors: FieldErrors, methods: HookFormMethods) => void;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getFirstErrorPath = (errors: FieldErrors, prefix = ''): string | undefined => {
  for (const [key, value] of Object.entries(errors)) {
    const path = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === 'object') {
      if ('message' in value || 'type' in value) return path;

      const nestedPath = getFirstErrorPath(value as FieldErrors, path);
      if (nestedPath) return nestedPath;
    }
  }
};

function HookFormResolver(props: HookFormResolverProps) {
  const methods = useForm({
    resolver: props.validations ? (yupResolver(props.validations) as any) : undefined,
    defaultValues: props.initialValues ? props.initialValues : {},
    mode: props.validationMode ? props.validationMode : 'onChange'
  });

  const { formState } = methods;

  const submit = async (data: any) => {
    if (props.onSubmit) {
      props.onSubmit(data, methods.reset);
    }

    await sleep(configHookForm.submitDelay);
  };

  const childrenProps: HookFormMethods = {
    ...methods
  };

  return (
    <FormProvider {...methods}>
      <Form
        noValidate
        autoComplete="off"
        id={props.nameForm}
        onSubmit={
          formState.isSubmitting
            ? (e) => {
                e.preventDefault();
              }
            : methods.handleSubmit(
                (data) => submit(data),
                (errors) => {
                  const firstErrorPath = getFirstErrorPath(errors);
                  if (firstErrorPath) methods.setFocus(firstErrorPath);
                  props.onInvalid?.(errors, methods);
                }
              )
        }>
        {props.children?.(childrenProps)}
      </Form>
    </FormProvider>
  );
}

export default HookFormResolver;
